import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle, XCircle } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Badge, Button, Card, Spinner } from '@/components/ui';

export function JoinPage() {
  const { code } = useParams<{ code: string }>();
  const { isAuthenticated } = useAuth();
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const returnPath = `/join/${code}`;
  const loginUrl = `/login?redirect=${encodeURIComponent(returnPath)}`;

  const { data, isLoading } = useQuery({
    queryKey: ['invite', code],
    queryFn: () =>
      api<{
        type: string;
        organization?: { name: string; description?: string };
        event?: { name: string; description?: string };
      }>(`/invites/${code}`),
    enabled: !!code,
    retry: false,
  });

  async function handleJoin() {
    if (!code) return;
    setLoading(true);
    setError('');
    try {
      const result = await api<{ status: string; type: string }>(`/invites/${code}/join`, {
        method: 'POST',
      });
      setStatus(result.status);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        if (err.status === 409) setStatus('pending');
      } else {
        setError('Katılım talebi gönderilemedi');
      }
    } finally {
      setLoading(false);
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  if (!data) {
    return (
      <Card className="text-center">
        <XCircle className="mx-auto mb-2 h-10 w-10 text-destructive" />
        <p>Geçersiz davet bağlantısı</p>
        <Link to="/dashboard">
          <Button className="mt-4">Ana Sayfa</Button>
        </Link>
      </Card>
    );
  }

  const title = data.organization?.name ?? data.event?.name ?? 'Program';
  const description = data.organization?.description ?? data.event?.description;
  const isOrgInvite = data.type === 'organization';

  return (
    <Card>
      <Badge className="mb-3">{isOrgInvite ? 'Organizasyon Daveti' : 'Program Daveti'}</Badge>
      <h2 className="text-xl font-semibold">{title}</h2>
      {description && <p className="mt-2 text-sm text-muted-foreground">{description}</p>}
      <p className="mt-3 text-xs text-muted-foreground">
        {isOrgInvite
          ? 'Bu link ile katıldığınızda organizasyondaki tüm programları görebilirsiniz.'
          : 'Bu link ile katıldığınızda yalnızca bu programı görebilirsiniz.'}
      </p>

      {!isAuthenticated ? (
        <div className="mt-6 space-y-2">
          <p className="text-sm text-muted-foreground">
            Katılmak için önce Google hesabınızla girin.
          </p>
          <Link to={loginUrl}>
            <Button className="w-full">Google ile gir</Button>
          </Link>
        </div>
      ) : status ? (
        <div className="mt-6 text-center">
          <CheckCircle className="mx-auto mb-2 h-10 w-10 text-success" />
          <p className="font-medium">
            {status === 'pending' ? 'Katılım talebiniz alındı' : 'Katılım durumu güncellendi'}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">Yönetici onayı bekleniyor</p>
          <Link to="/dashboard">
            <Button className="mt-4">Ana Sayfa</Button>
          </Link>
        </div>
      ) : (
        <div className="mt-6">
          {error && <p className="mb-3 text-sm text-destructive">{error}</p>}
          <Button className="w-full" onClick={handleJoin} disabled={loading}>
            {loading ? <Spinner /> : 'Katılım Talebi Gönder'}
          </Button>
        </div>
      )}
    </Card>
  );
}
